import getComponentMarkup from '@helpers/getComponentMarkup';
import { ComponentProps } from 'react';
import VideoOverlayComponent from '@molecules/video-overlay/VideoOverlay';
import getVideoSrc from '@helpers/getVideoSrc';
import { getClassFunction } from '@helpers/functions';
import { VideoSourceModel } from '@_types/organisms';

const closeOverlayEvent = 'close-video-overlay-event';
const getClass = getClassFunction('kf-o-video-overlay');

class VideoOverlay {
  private overlay: HTMLElement | null = null;

  constructor() {
    this.addVideoOverlay();
    this.addCloseOverlayEvent();

    this._iframe?.addEventListener('load', () => {
      this.toggleSiteLoading(false);
      this.toggleIframeVisibility(true);
    });
  }

  get _iframe() {
    return this.overlay?.querySelector<HTMLIFrameElement>('iframe');
  }

  get _overlay() {
    return this.overlay;
  }

  toggleSiteLoading(visible: boolean) {
    const siteLoading = this.overlay?.querySelector<HTMLElement>('.kf-a-site-loading');

    if (!siteLoading) return;

    if (visible) {
      siteLoading.style.display = 'block';
    } else {
      siteLoading.style.display = 'none';
    }
  }

  toggleIframeVisibility(visible: boolean) {
    if (!this._iframe) return;

    if (visible) {
      this._iframe.style.display = 'block';
    } else {
      this._iframe.style.display = 'none';
    }
  }

  addVideoOverlay() {
    const overlayContainer = document.getElementById('video-overlay-container');

    if (!overlayContainer || this.overlay) return;

    overlayContainer.innerHTML = getComponentMarkup<ComponentProps<typeof VideoOverlayComponent>>(VideoOverlayComponent, {
      videoId: '',
    });

    this.overlay = overlayContainer.querySelector(getClass());
  }

  setVideoOverlayData(source: VideoSourceModel, videoId: string) {
    if (!this.overlay) return;

    const currentSource = this.overlay.dataset.source as VideoSourceModel;
    const currentVideoId = this.overlay.dataset.videoId;

    if (currentVideoId !== videoId || currentSource !== source) {
      const newSrc = getVideoSrc(videoId, source);

      this.overlay.setAttribute('data-source', source);
      this.overlay.setAttribute('data-video-id', videoId);

      if (!this._iframe) return;

      this.toggleIframeVisibility(false);
      this.toggleSiteLoading(true);

      this._iframe.setAttribute('src', newSrc || '');
    }
  }

  addCloseOverlayEvent() {
    if (!this.overlay) return;
    const closeButton = this.overlay.querySelector<HTMLElement>(getClass('close-icon'));

    if (!closeButton) return;

    closeButton.addEventListener('click', () => {
      this.toggleVideoOverlay(false);
    });

    closeButton.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this.toggleVideoOverlay(false);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.toggleVideoOverlay(false);
      }
    });
  }

  toggleVideoOverlay(isOpen: boolean) {
    if (!this.overlay) return;

    if (isOpen) {
      this.overlay.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      this.overlay.querySelector<HTMLElement>(getClass('close-icon'))?.focus();
    } else {
      window.dispatchEvent(new CustomEvent(closeOverlayEvent));
      this.overlay.style.display = 'none';

      document.body.style.overflow = 'auto';
    }
  }
}

const videoOverlayWrapper: {
  videoOverlay?: VideoOverlay;
} = {};

document.addEventListener('DOMContentLoaded', () => {
  videoOverlayWrapper.videoOverlay = new VideoOverlay();
});

export { videoOverlayWrapper, closeOverlayEvent };
